import React, { useState, useEffect, useMemo } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import ImagePositionEditor from './ImagePositionEditor';
import { getAllCourses, getSpecialties, resolveFeaturedProgramLink } from '../../utils/formationCatalog';
import { resolveWhatsAppPhone } from '../../utils/whatsapp';
import { getMediaUrl } from '../../utils/mediaUrl';
import './FeaturedProgramsAdmin.css';

const FeaturedProgramsAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const courseOptions = useMemo(() => {
    const specialties = getSpecialties(getContent);
    const courses = getAllCourses(getContent);
    return specialties.flatMap((specialty) =>
      courses
        .filter((course) => course.specialtyId === specialty.id)
        .map((course) => ({
          id: course.id,
          label: `${specialty.title} — ${course.title}`,
        }))
    );
  }, [getContent]);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleProgramUpdate = (index, field, value) => {
    updateContent(`${contentPath}.programs.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleImagePositionSave = (index, settings) => {
    updateContent(`${contentPath}.programs.${index}.imageSettings`, settings);
    setSectionData(getContent(contentPath));
    setEditingImageIndex(null);
  };

  const handleFeatureUpdate = (programIndex, featureIndex, value) => {
    updateContent(`${contentPath}.programs.${programIndex}.features.${featureIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddFeature = (programIndex) => {
    const program = sectionData.programs[programIndex];
    const features = program.features || [];
    updateContent(`${contentPath}.programs.${programIndex}.features`, [...features, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteFeature = (programIndex, featureIndex) => {
    const program = sectionData.programs[programIndex];
    const features = program.features.filter((_, i) => i !== featureIndex);
    updateContent(`${contentPath}.programs.${programIndex}.features`, features);
    setSectionData(getContent(contentPath));
  };

  const handleAddProgram = () => {
    const programs = sectionData?.programs || [];
    const newProgram = {
      id: '',
      title: '',
      description: '',
      icon: 'fas fa-tooth',
      image: '',
      imageObjectFit: 'contain', // Default to 'contain' to show full image
      badge: '',
      features: [],
      link: '',
      courseId: '',
      whatsappMessage: ''
    };

    updateContent(`${contentPath}.programs`, [...programs, newProgram]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteProgram = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) {
      const programs = sectionData?.programs || [];
      const updatedPrograms = programs.filter((_, i) => i !== index);
      updateContent(`${contentPath}.programs`, updatedPrograms);
      setSectionData(getContent(contentPath));
    }
  };

  const handleMoveProgram = (index, direction) => {
    const programs = [...(sectionData?.programs || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= programs.length) return;

    [programs[index], programs[targetIndex]] = [programs[targetIndex], programs[index]];
    updateContent(`${contentPath}.programs`, programs);
    setSectionData(getContent(contentPath));
  };

  const programCount = sectionData?.programs?.length || 0;

  if (!sectionData) {
    return (
      <div className="programs-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="programs-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-briefcase-medical"></i>
            Programmes Phares
          </h2>
          <p>Gérez les programmes de formation mis en avant sur votre site web</p>
        </div>
        <button 
          className="btn-add-program"
          onClick={handleAddProgram}
        >
          <i className="fas fa-plus"></i>
          Ajouter un programme
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Nos Formations Phares"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Description des programmes phares..."
            rows={2}
          />
        </div>
      </div>

      {/* Programs List */}
      <div className="programs-list-admin">
        {(sectionData.programs || []).map((program, index) => (
          <div key={index} className="program-item-admin">
            <div className="program-header">
              <div className="program-title-area">
                <h3>
                  <i className="fas fa-tooth"></i>
                  Programme {index + 1}
                </h3>
                {program.badge && (
                  <span className="program-badge">
                    {program.badge}
                  </span>
                )}
              </div>
              <div className="program-actions">
                <button
                  type="button"
                  className="btn-move"
                  onClick={() => handleMoveProgram(index, 'up')}
                  disabled={index === 0}
                  title="Remonter"
                  aria-label="Remonter ce programme"
                >
                  <i className="fas fa-arrow-up"></i>
                </button>
                <button
                  type="button"
                  className="btn-move"
                  onClick={() => handleMoveProgram(index, 'down')}
                  disabled={index === programCount - 1}
                  title="Descendre"
                  aria-label="Descendre ce programme"
                >
                  <i className="fas fa-arrow-down"></i>
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteProgram(index)}
                  title="Supprimer ce programme"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Clean Horizontal Form Fields */}
            <div className="form-grid">
              <div className="form-group">
                <label>ID</label>
                <input
                  type="text"
                  value={program.id || ''}
                  onChange={(e) => handleProgramUpdate(index, 'id', e.target.value)}
                  placeholder="cone-beam"
                />
              </div>

              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={program.title || ''}
                  onChange={(e) => handleProgramUpdate(index, 'title', e.target.value)}
                  placeholder="Formation Cone Beam CBCT"
                />
              </div>

              <div className="form-group">
                <label>Icône (optionnel, utilisé si pas d'image)</label>
                <select
                  value={program.icon || 'fas fa-tooth'}
                  onChange={(e) => handleProgramUpdate(index, 'icon', e.target.value)}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <optgroup label="Dentaire">
                    <option value="fas fa-tooth">🦷 Dent (fa-tooth)</option>
                    <option value="fas fa-teeth">🦷 Dents (fa-teeth)</option>
                    <option value="fas fa-teeth-open">😬 Dents ouvertes (fa-teeth-open)</option>
                  </optgroup>
                  <optgroup label="Médical">
                    <option value="fas fa-x-ray">🩻 Radiographie (fa-x-ray)</option>
                    <option value="fas fa-stethoscope">🩺 Stéthoscope (fa-stethoscope)</option>
                    <option value="fas fa-briefcase-medical">💼 Mallette médicale (fa-briefcase-medical)</option>
                    <option value="fas fa-notes-medical">📋 Notes médicales (fa-notes-medical)</option>
                    <option value="fas fa-user-md">👨‍⚕️ Docteur (fa-user-md)</option>
                    <option value="fas fa-hospital">🏥 Hôpital (fa-hospital)</option>
                    <option value="fas fa-first-aid">🩹 Premier secours (fa-first-aid)</option>
                    <option value="fas fa-syringe">💉 Seringue (fa-syringe)</option>
                    <option value="fas fa-prescription-bottle">💊 Médicament (fa-prescription-bottle)</option>
                  </optgroup>
                  <optgroup label="Éducation">
                    <option value="fas fa-graduation-cap">🎓 Diplôme (fa-graduation-cap)</option>
                    <option value="fas fa-chalkboard-teacher">👨‍🏫 Professeur (fa-chalkboard-teacher)</option>
                    <option value="fas fa-book">📚 Livre (fa-book)</option>
                    <option value="fas fa-certificate">📜 Certificat (fa-certificate)</option>
                    <option value="fas fa-award">🏆 Récompense (fa-award)</option>
                    <option value="fas fa-medal">🥇 Médaille (fa-medal)</option>
                  </optgroup>
                  <optgroup label="Technologie">
                    <option value="fas fa-laptop-medical">💻 Ordinateur médical (fa-laptop-medical)</option>
                    <option value="fas fa-microscope">🔬 Microscope (fa-microscope)</option>
                    <option value="fas fa-vial">🧪 Tube à essai (fa-vial)</option>
                    <option value="fas fa-flask">🧪 Flacon (fa-flask)</option>
                  </optgroup>
                  <optgroup label="Pratique">
                    <option value="fas fa-calendar-alt">📅 Calendrier (fa-calendar-alt)</option>
                    <option value="fas fa-clock">🕐 Horloge (fa-clock)</option>
                    <option value="fas fa-map-marker-alt">📍 Localisation (fa-map-marker-alt)</option>
                    <option value="fas fa-phone">📞 Téléphone (fa-phone)</option>
                    <option value="fas fa-envelope">✉️ Email (fa-envelope)</option>
                    <option value="fas fa-user">👤 Utilisateur (fa-user)</option>
                    <option value="fas fa-users">👥 Groupe (fa-users)</option>
                  </optgroup>
                  <optgroup label="Général">
                    <option value="fas fa-star">⭐ Étoile (fa-star)</option>
                    <option value="fas fa-heart">❤️ Cœur (fa-heart)</option>
                    <option value="fas fa-check-circle">✅ Check (fa-check-circle)</option>
                    <option value="fas fa-lightbulb">💡 Ampoule (fa-lightbulb)</option>
                    <option value="fas fa-crown">👑 Couronne (fa-crown)</option>
                    <option value="fas fa-gem">💎 Diamant (fa-gem)</option>
                    <option value="fas fa-rocket">🚀 Fusée (fa-rocket)</option>
                  </optgroup>
                </select>
                <div style={{marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f8f9fa', borderRadius: '8px'}}>
                  <span style={{fontSize: '12px', color: '#666'}}>Aperçu:</span>
                  <i className={program.icon || 'fas fa-tooth'} style={{fontSize: '24px', color: '#3498db'}}></i>
                  <span style={{fontSize: '12px', color: '#999', fontFamily: 'monospace'}}>{program.icon || 'fas fa-tooth'}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Badge</label>
                <input
                  type="text"
                  value={program.badge || ''}
                  onChange={(e) => handleProgramUpdate(index, 'badge', e.target.value)}
                  placeholder="Formation Phare"
                />
              </div>

              <div className="form-group full-width">
                <label>Image du Programme</label>
                <ImageUpload
                  value={program.image}
                  onChange={(url) => handleProgramUpdate(index, 'image', url)}
                  label="Télécharger une image"
                />
                
                {program.image && (
                  <div style={{ marginTop: '1rem' }}>
                    {editingImageIndex === index ? (
                      <ImagePositionEditor
                        imageUrl={getMediaUrl(program.image)}
                        currentSettings={program.imageSettings}
                        onSave={(settings) => handleImagePositionSave(index, settings)}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingImageIndex(index)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px dashed #007bff',
                          borderRadius: '0.5rem',
                          background: '#e7f3ff',
                          color: '#007bff',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-sliders-h"></i> Ajuster la position et le zoom de l'image
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={program.description || ''}
                  onChange={(e) => handleProgramUpdate(index, 'description', e.target.value)}
                  placeholder="Formation de référence en imagerie Cone Beam..."
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Formation liée (catalogue)</label>
                <select
                  value={program.courseId || ''}
                  onChange={(e) => {
                    const courseId = e.target.value;
                    const programs = [...(sectionData.programs || [])];
                    programs[index] = {
                      ...programs[index],
                      courseId,
                      link: courseId
                        ? resolveFeaturedProgramLink({ ...program, courseId }, getContent)
                        : program.link,
                    };
                    updateContent(`${contentPath}.programs`, programs);
                    setSectionData(getContent(contentPath));
                  }}
                >
                  <option value="">— Sélectionner une formation —</option>
                  {courseOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Lien (généré ou personnalisé)</label>
                <input
                  type="text"
                  value={program.link || ''}
                  onChange={(e) => handleProgramUpdate(index, 'link', e.target.value)}
                  placeholder="/formations/specialite/formation"
                />
              </div>

              <div className="form-group full-width">
                <label>Message WhatsApp</label>
                <textarea
                  value={program.whatsappMessage || ''}
                  onChange={(e) => handleProgramUpdate(index, 'whatsappMessage', e.target.value)}
                  placeholder="Bonjour, je souhaite des informations sur la formation : ..."
                  rows={2}
                />
              </div>

              <div className="form-group full-width">
                <label>Numéro WhatsApp pour ce programme</label>
                <input
                  type="tel"
                  value={program.whatsappPhone || ''}
                  onChange={(e) => handleProgramUpdate(index, 'whatsappPhone', e.target.value)}
                  placeholder={resolveWhatsAppPhone(program, getContent) || '+213797607481'}
                />
                <small style={{ color: '#64748b' }}>
                  Indicatif pays inclus (ex: +213797607481). Laissez vide pour utiliser le numéro principal.
                </small>
              </div>

              {/* Features Section */}
              <div className="form-group full-width">
                <div className="features-section">
                  <div className="features-header">
                    <label>Fonctionnalités</label>
                    <button
                      type="button"
                      className="btn-add-feature"
                      onClick={() => handleAddFeature(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="features-list">
                    {(program.features || []).map((feature, featureIndex) => (
                      <div key={featureIndex} className="feature-item">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => handleFeatureUpdate(index, featureIndex, e.target.value)}
                          placeholder="Fonctionnalité..."
                        />
                        <button
                          type="button"
                          className="btn-delete-feature"
                          onClick={() => handleDeleteFeature(index, featureIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!sectionData.programs || sectionData.programs.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-briefcase-medical"></i>
          </div>
          <h3>Aucun programme phare</h3>
          <p>Commencez par ajouter votre premier programme phare.</p>
          <button className="btn-add-first" onClick={handleAddProgram}>
            <i className="fas fa-plus"></i>
            Ajouter le premier programme
          </button>
        </div>
      )}
    </div>
  );
};

export default FeaturedProgramsAdmin;



